/**
 * TrackingPathPanel — one subject's trail drawn WHERE IT HAPPENED, docked under the Track tab.
 *
 * ─── WHY THIS REPLACED THE TIMELINE STRIP ───
 *
 * The strip answers "where in the day was the activity", and on the Search tab that is the
 * question the answer above it cannot answer for itself. On the Track tab it is the wrong
 * question twice over: a trail already reads in time order, stop by stop, with the dwell and
 * the gap written between them — so a chart of events per hour restates the one thing the
 * answer says best, and says nothing about the thing it cannot say at all.
 *
 * WHICH IS WHERE. "Gate 1 → Reception → Server Room" is a list of names an operator has to
 * hold a building in their head to read. The same three stops on a plan are a route: through
 * the lobby, past the desk, and into a room they should not have reached. That is what this
 * panel is for, and it is the only part of following somebody that a list genuinely cannot do.
 *
 * ─── IT DRAWS WHAT THE TIMELINE LISTS ───
 *
 * The same stops, in the same order, from the same fold (`buildStops`, exported by
 * ResultTimeline for exactly this). Line for line, what the operator is reading above is what
 * is drawn below, so the plan can never show a stop the list does not — which a second fold
 * with its own idea of where a visit ends would do within a release.
 *
 * It used to draw ONE SELECTED SUBJECT out of a page of trails. That is gone with the trails:
 * the tab's job is "where did this go", and making the operator pick an individual before it
 * would show them a route was a click in front of the answer.
 *
 * WHICH MEANS IT CAN BE ASKED A QUESTION IT MUST REFUSE. A page of stops belonging to six
 * different ENROLLED people is not a route, and a line through it would join one person's
 * doorway to another's. The panel counts the identities in the stops it is given and says so
 * instead of drawing — see `identities` below.
 *
 * An attribute search is NOT that case, and treating it as one was a bug: "red top, black
 * trousers" produces no identity at all, only a per-camera tracker id that differs at every
 * camera, so the old count read one person's walk through a building as six strangers and
 * refused the one drawing the operator had come for. Those trails are drawn now, captioned as
 * a description rather than a person — see `byDescription`.
 *
 * ─── TWO CANVASES, ONE PATH ───
 *
 * GIS (the floor plan) and Map (the geographic basemap) answer different questions — "which
 * door" against "which building" — and which one a trail wants depends on the trail. So the
 * panel offers both and OPENS ON WHICHEVER ACTUALLY HOLDS MORE OF THIS TRAIL'S CAMERAS: an
 * indoor route opens on the plan, a cross-site one on the map, without anybody choosing.
 *
 * A canvas with none of the trail's cameras on it is offered but disabled, with the reason in
 * its tooltip — hiding it would leave an operator wondering whether the product has the view
 * at all, which is a different question from whether these cameras are on it.
 *
 * ─── STOPS THAT CANNOT BE DRAWN ARE COUNTED, NEVER DROPPED SILENTLY ───
 *
 * A camera with no pin and no coordinates cannot be on either canvas. The path then joins the
 * stops it can place, which is a route with a piece missing — and a route with a piece missing
 * read as a whole route is the failure this panel would otherwise introduce. So the header
 * always states how many of the trail's stops are drawn out of how many there are, and the
 * shortfall is named.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  GlobeAltIcon,
  MapIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import GeoTileMap, { type GeoTileMapHandle } from '../map/GeoTileMap';
import BasemapSwitcher from '../map/BasemapSwitcher';
import { useBasemap } from '../map/basemaps';
import DockShell from './DockShell';
import { useApi } from '../../api/ApiProvider';
import type {
  GeoMapConfig,
  GeoPlacement,
  MapLayoutDetail,
  MapLayoutSummary,
  MapPlacedCamera,
} from '../../api/contracts';
import type { Stop } from './ResultTimeline';

export type PathView = 'gis' | 'map';

/**
 * The dock's chrome — fold, drag-to-resize, full screen — is `DockShell`, shared with the Search
 * tab's timeline. It used to live here, which is why the timeline had no drag and this had no
 * full screen: chrome written twice is chrome that diverges.
 *
 * `ceilingFrom` moved with it and is re-exported so `scripts/test-tracking-path.ts` keeps
 * asserting the height arithmetic from the file it has always imported.
 */
export { ceilingFrom } from './DockShell';

/** One drawable stop: where it is, and which positions on the trail it accounts for. */
export interface PathNode {
  cameraId: string;
  x: number;
  y: number;
  /**
   * The stop numbers this point stands for, 1-based over the WHOLE trail.
   *
   * A trail that returns to a camera has two stops at one position, and two pins on one spot
   * is a number nobody can read. They share a pin and the pin carries both — which is also the
   * truer statement: the subject was at that place twice.
   *
   * Numbered over the whole trail, not over the drawable ones, so an undrawable stop leaves a
   * gap in the numbering rather than silently renumbering the rest and making the path look
   * complete.
   */
  ordinals: number[];
}

/**
 * One camera-to-camera move: a leg of the journey, drawn as its own line.
 *
 * ── Why edges and not one polyline ──
 *
 * A polyline through the stops in order is the obvious drawing and it loses the thing the panel
 * exists for. Gate 1 → Lobby → Gate 1 traces the second leg back over the first, pixel for
 * pixel, so a there-and-back reads as a single line and a there-and-stayed reads identically to
 * it. The same is true of any corridor walked twice — the busiest edges are exactly the ones a
 * polyline hides.
 *
 * So each traversal is its own edge, bowed to one side, and repeated traversals of the same pair
 * fan out beside each other. The direction decides WHICH side: A→B always bows one way and B→A
 * the other, so an out-and-back is two arcs making a lens and the operator can see both legs and
 * which way each ran.
 */
export interface PathEdge {
  from: PathNode;
  to: PathNode;
  /** 1-based position in the trail — the move INTO `to`, so it lands on that stop's number. */
  seq: number;
  /** How far, and which way, this leg bows off the straight chord. See `layEdges`. */
  bow: number;
}

/** The path in draw order, plus what it could not place. */
interface Path {
  nodes: PathNode[];
  /** Points in trail order — a return to one camera appears twice, as it should. */
  line: Array<{ x: number; y: number }>;
  /** Every move between two DIFFERENT placed cameras, in order. */
  edges: PathEdge[];
  placed: number;
  total: number;
}

/**
 * Spread the legs so every one of them can be seen.
 *
 * `bow` is a FRACTION of each leg's own length, applied along the perpendicular, so a long
 * corridor and a short hop bow by proportionate amounts rather than the short one curling into
 * a loop.
 *
 * The sign comes from comparing the two camera ids, not from the order of travel, so the same
 * pair always bows to the same two sides — otherwise which side a leg took would depend on
 * which end of the trail the operator happened to be looking at.
 */
export function layEdges(nodes: PathNode[], order: string[]): PathEdge[] {
  const byId = new Map(nodes.map((n) => [n.cameraId, n]));
  const seen = new Map<string, number>();
  const edges: PathEdge[] = [];

  for (let i = 1; i < order.length; i += 1) {
    const from = byId.get(order[i - 1]);
    const to = byId.get(order[i]);
    // A stop split from its neighbour by the gap rule, at the same camera, is not a MOVE. The
    // pin's own ordinals ("1,2") already say the subject was there twice.
    if (!from || !to || from === to) continue;
    /**
     * Counted per DIRECTION, not per pair.
     *
     * Per pair, an out-and-back came out as 0.13 and 0.26 — a lopsided lens in which the return
     * leg swung twice as wide as the outbound for no reason a reader could name. The first walk
     * each way should be the same distance off the chord; only a REPEAT of the same direction
     * needs to step further out to clear the one before it.
     */
    const pair = `${from.cameraId}|${to.cameraId}`;
    const nth = seen.get(pair) || 0;
    seen.set(pair, nth + 1);
    const side = from.cameraId < to.cameraId ? 1 : -1;
    edges.push({ from, to, seq: i + 1, bow: side * 0.13 * (nth + 1) });
  }
  return edges;
}

/** A bowed leg as an SVG path, and where its arrowhead sits on it. */
export function edgeGeometry(e: PathEdge) {
  const dx = e.to.x - e.from.x;
  const dy = e.to.y - e.from.y;
  const len = Math.hypot(dx, dy) || 1;
  // The control point, out along the perpendicular. A quadratic through it passes half as far
  // off the chord as the control point sits, which is why the bow is doubled here.
  const cx = (e.from.x + e.to.x) / 2 - (dy / len) * len * e.bow * 2;
  const cy = (e.from.y + e.to.y) / 2 + (dx / len) * len * e.bow * 2;
  return {
    d: `M ${e.from.x} ${e.from.y} Q ${cx} ${cy} ${e.to.x} ${e.to.y}`,
    // The curve's midpoint (t = 0.5). Its tangent there is parallel to the chord, which is what
    // lets the arrowhead take its angle straight from dx/dy.
    mx: 0.25 * e.from.x + 0.5 * cx + 0.25 * e.to.x,
    my: 0.25 * e.from.y + 0.5 * cy + 0.25 * e.to.y,
    angle: (Math.atan2(dy, dx) * 180) / Math.PI,
  };
}

function buildPath(
  stops: Stop[],
  positionOf: (cameraId: string) => { x: number; y: number } | null,
): Path {
  const byCamera = new Map<string, PathNode>();
  const line: Array<{ x: number; y: number }> = [];
  let placed = 0;

  stops.forEach((stop, i) => {
    const at = positionOf(stop.cameraId);
    if (!at) return;
    placed += 1;
    line.push(at);
    const seen = byCamera.get(stop.cameraId);
    if (seen) seen.ordinals.push(i + 1);
    else byCamera.set(stop.cameraId, { cameraId: stop.cameraId, x: at.x, y: at.y, ordinals: [i + 1] });
  });

  const nodes = [...byCamera.values()];
  const order = stops.map((st) => st.cameraId).filter((id) => byCamera.has(id));
  return { nodes, line, edges: layEdges(nodes, order), placed, total: stops.length };
}

/**
 * WHERE THE PLAN IS DRAWN INSIDE THE CONTAINER.
 *
 * ─── WHY THE WHOLE PLAN, FITTED ───
 *
 * This used to crop: a box around the route, shaped like the dock, magnified to fill it. That
 * answers "show me the route" and gets two other things wrong. A crop tight enough to be useful
 * blows an uploaded floor plan up past the resolution it was scanned at, and — because the
 * placements are fractions of the stored `image_width × image_height`, which the drawing has to
 * honour for a pin to land on its door — any disagreement between those numbers and the file is
 * magnified with it. Both read as "the plan is not rendering properly", and both were.
 *
 * So the plan is drawn WHOLE, scaled to fit the container and centred in it, at its own
 * proportions. The operator sees the building; the dock is draggable and goes full screen when
 * they want it bigger. What is left over is canvas, not a stretched picture.
 *
 * Everything else on the canvas — the pins, the legs, the arrowheads, the labels — is positioned
 * against THIS rectangle rather than against the plan's stored pixel size, so the route is drawn
 * wherever the plan ended up and at whatever size it ended up.
 *
 * Exported for `scripts/test-tracking-path.ts`.
 */
export interface PlanRect { x: number; y: number; w: number; h: number; scale: number }

export function fitPlan(
  container: { w: number; h: number },
  image: { w: number; h: number },
): PlanRect | null {
  if (!(container.w > 0 && container.h > 0 && image.w > 0 && image.h > 0)) return null;
  // `min`, so the whole plan is inside the box on both axes — `max` would cover the container
  // and cut the plan, which is the crop this replaced.
  const scale = Math.min(container.w / image.w, container.h / image.h);
  const w = image.w * scale;
  const h = image.h * scale;
  return { x: (container.w - w) / 2, y: (container.h - h) / 2, w, h, scale };
}

/** A placement (fractions of the plan) at its pixel position on the drawn rectangle. */
export function placeOn(rect: PlanRect, at: { x: number; y: number }) {
  return { x: rect.x + at.x * rect.w, y: rect.y + at.y * rect.h };
}

export interface TrackingPathPanelProps {
  /**
   * The stops the timeline is listing, in TIME order (oldest first) — `buildStops`' own order,
   * before the list applies its reading direction. A route is what happened, so it is drawn
   * forwards whichever way the list above is being read.
   */
  stops: Stop[];
  cameraName: (id: string) => string | undefined;
  /** Changes when the operator switches node (cloud) — drops every cached placement. */
  nodeKey?: string;
}

export default function TrackingPathPanel({
  stops, cameraName, nodeKey = '',
}: TrackingPathPanelProps) {
  const { mapLayouts, geoMap } = useApi();

  /**
   * The canvas the operator is on. Null until they choose, so `active` below can open on
   * whichever canvas actually holds more of THIS trail.
   */
  const [view, setView] = useState<PathView | null>(null);

  const [placedCameras, setPlacedCameras] = useState<Record<string, MapPlacedCamera> | null>(null);
  const [layouts, setLayouts] = useState<MapLayoutSummary[]>([]);
  const [detail, setDetail] = useState<MapLayoutDetail | null>(null);
  const [planUrl, setPlanUrl] = useState<string | null>(null);
  /**
   * The plan fetch has FINISHED, whether or not it produced a plan.
   *
   * Needed separately from `detail` because a failed fetch leaves `detail` null, which is
   * indistinguishable from a fetch still running — and the panel draws two different things for
   * those: a spinner, or a verdict that nothing here is placed. Without this, a layout that
   * 404s pinned the panel on "Loading…" for ever.
   */
  const [layoutSettled, setLayoutSettled] = useState(false);
  const [geo, setGeo] = useState<GeoPlacement[] | null>(null);
  const [geoConfig, setGeoConfig] = useState<GeoMapConfig | null>(null);
  // Same choice and storage as the Alert Board's Map View — one preference per browser.
  const basemap = useBasemap(geoConfig);
  const [error, setError] = useState<string | null>(null);

  const mapRef = useRef<GeoTileMapHandle>(null);

  const cameraIds = useMemo(
    () => [...new Set(stops.map((st) => st.cameraId))],
    [stops],
  );

  /**
   * How many IDENTITIES these stops belong to — see `Stop.identified`.
   *
   * A route is one thing moving. Two enrolled people's stops joined by a line is not a route, it
   * is a line between strangers, and it reads as one person's movement because that is what a
   * line between pins means. That refusal stands.
   *
   * WHAT IT USED TO COUNT was tracker keys, and a tracker id is unique only within one camera.
   * So an attribute search — "red top, black trousers", the thing the Track tab is most useful
   * for — produced a fresh key at every camera the person passed, reported "6 different
   * subjects", and refused to draw the one answer it was asked for. The path was reachable only
   * by name or by plate, which is precisely the case where the operator already knows who they
   * are following.
   */
  const identities = useMemo(
    () => new Set(stops.filter((st) => st.identified).map((st) => st.subjectId)).size,
    [stops],
  );
  /** Separate tracks, identified or not — what `byDescription` counts to be honest about. */
  const tracks = useMemo(
    () => new Set(stops.filter((st) => st.tracked).map((st) => st.subjectId)).size,
    [stops],
  );
  /** One identity, or none to speak of — drawable either way. Two or more is not a route. */
  const oneSubject = identities <= 1;
  /**
   * The trail is a DESCRIPTION's, not a person's: every stop matched the filters, and nothing in
   * the data says they are the same individual.
   *
   * Drawn, because it is the answer to the question that was asked, and captioned, because the
   * line would otherwise assert an identity the system cannot support.
   */
  const byDescription = oneSubject && identities === 0 && tracks > 1;
  /** What to call the thing being followed in that caption. */
  const subjectWord = stops.find((st) => st.subjectKind)?.subjectKind === 'vehicle'
    ? 'vehicle' : 'person';

  /**
   * ── LOOKUPS, AND WHAT IS DELIBERATELY NOT FETCHED ──────────────────────
   *
   * Only once the panel is open, and only three cheap reads: where every placed camera sits,
   * the layout NAMES, and the geographic placements plus basemap configuration.
   *
   * NO PLAN IMAGE IS FETCHED HERE. A site can hold a dozen floor plans and each is a picture;
   * loading them to find out which ones matter would spend megabytes to answer a question the
   * placements already answer for free. The side nav is built from `layoutsOnRoute` — the plans
   * this trail actually has stops on — and exactly one image is then fetched: the plan being
   * drawn. Switching plans fetches that one, and the adapter caches it per layout+updated_at, so
   * going back and forth costs nothing.
   */
  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    (async () => {
      try {
        const [placed, list, geoPlacements, cfg] = await Promise.all([
          mapLayouts.placedCameras().catch(() => ({} as Record<string, MapPlacedCamera>)),
          mapLayouts.list().catch(() => [] as MapLayoutSummary[]),
          geoMap.placements().catch(() => [] as GeoPlacement[]),
          geoMap.config().catch(() => null),
        ]);
        if (!live) return;
        setPlacedCameras(placed);
        setLayouts(list);
        setGeo(geoPlacements);
        setGeoConfig(cfg);
      } catch (err: any) {
        // One failed lookup must not take the panel down — each canvas reports itself as
        // having nothing to draw, which is what an operator can act on.
        if (live) setError(err?.message || 'Could not load camera placements');
      }
    })();
    return () => { live = false; };
  }, [open, mapLayouts, geoMap, nodeKey]);

  /**
   * WHICH PLANS THIS ROUTE TOUCHES, and how much of it each one holds.
   *
   * A camera belongs to exactly one layout, so a route through a building crosses floors and no
   * single plan can hold all of it. Every plan carrying at least one stop is therefore a real
   * answer to "where did they go", and the panel offers all of them rather than picking one and
   * hiding the rest — which is what it did, and which made a two-floor route look like a
   * one-floor route with stops missing.
   *
   * Ordered by coverage so the fullest answer is first in the list.
   */
  const layoutsOnRoute = useMemo(() => {
    if (!placedCameras) return [] as Array<{ id: string; name: string; stops: number }>;
    const tally = new Map<string, { id: string; name: string; stops: number }>();
    // Counted over the STOPS, not the distinct cameras: a plan holding one camera the subject
    // returned to three times holds three of the route's stops, and that is what the numbers
    // beside the names are comparing.
    for (const st of stops) {
      const p = placedCameras[st.cameraId];
      if (!p) continue;
      const row = tally.get(p.layout_id);
      if (row) row.stops += 1;
      // The name off the placement itself, so the picker is still labelled when `list()` failed
      // — the two calls are independent and one of them going down must not leave a dropdown of
      // blank rows.
      else tally.set(p.layout_id, { id: p.layout_id, name: p.layout_name || 'Floor plan', stops: 1 });
    }
    return [...tally.values()]
      .sort((a, b) => b.stops - a.stops || a.name.localeCompare(b.name));
  }, [placedCameras, stops]);

  /** The operator's pick, once they have made one. Null until then. */
  const [chosenLayout, setChosenLayout] = useState<string | null>(null);

  /**
   * The plan the panel opens on: THE DEPLOYMENT'S DEFAULT, when the route touches it.
   *
   * Most-covered was the old rule and it was the wrong default. The default layout is the one
   * the site's own people chose as the place to look — the ground floor, the yard — so opening
   * anywhere else means the first thing an operator sees is a plan they have to re-orient
   * themselves on. Coverage only breaks the tie when the route never reaches the default.
   */
  const bestLayoutId = useMemo(() => {
    if (!layoutsOnRoute.length) return null;
    if (chosenLayout && layoutsOnRoute.some((l) => l.id === chosenLayout)) return chosenLayout;
    /**
     * The default layout, but only where it can show a ROUTE — two stops or more.
     *
     * Preferring it outright was a regression. The default is the plan the site opens on, which
     * on a multi-floor estate is the ground floor, and a route that merely passes through
     * reception has ONE stop there. The panel then opened on a plan with a single pin: no line
     * to draw, and the frame zoomed into a patch of wall around it. Both of those read as the
     * canvas being broken, and neither was — it was being shown the wrong plan.
     *
     * So coverage wins wherever the default cannot draw a connection. `layoutsOnRoute` is
     * already sorted by coverage, so its head is the fullest answer available.
     */
    const preferred = layouts.find((l) => l.is_default
      && (layoutsOnRoute.find((r) => r.id === l.id)?.stops || 0) >= 2);
    return preferred?.id || layoutsOnRoute[0].id;
  }, [layoutsOnRoute, layouts, chosenLayout]);

  useEffect(() => {
    if (!open || !bestLayoutId) {
      setDetail(null); setPlanUrl(null); setLayoutSettled(true);
      return undefined;
    }
    let live = true;
    // Re-armed on every change of layout: the trail moved, and what is on screen describes the
    // previous one until this resolves.
    setLayoutSettled(false);
    /**
     * DROP THE OLD PLAN BEFORE FETCHING THE NEW ONE.
     *
     * `detail` lands one await before the image does, and the image is drawn with
     * `preserveAspectRatio="none"` into `image_width × image_height` — the NEW plan's box. Left
     * up, the previous floor's image spent that gap stretched to the next floor's dimensions
     * with the next floor's pins on top of it: the wrong building, the wrong shape, and pins
     * sitting on nothing. Cheaper to show the pins on an empty canvas for one request than to
     * show a picture that is actively lying about where the subject went.
     */
    setPlanUrl(null);
    (async () => {
      try {
        const d = await mapLayouts.get(bestLayoutId);
        if (!live) return;
        setDetail(d);
        const url = await mapLayouts.imageUrl(d);
        if (live) setPlanUrl(url);
      } catch {
        // The pins still draw on a blank plan — knowing the shape of the route is most of it —
        // so only the IMAGE is given up here. A failed `get` leaves no placements either, and
        // `layoutSettled` is what stops that reading as "still loading".
        if (live) setPlanUrl(null);
      } finally {
        if (live) setLayoutSettled(true);
      }
    })();
    return () => { live = false; };
  }, [open, bestLayoutId, mapLayouts]);

  // ── The two paths ───────────────────────────────────────────────────────
  const gisPath = useMemo<Path | null>(() => {
    if (!stops.length || !detail) return null;
    const by = new Map(detail.placements.map((p) => [p.camera_id, p]));
    return buildPath(stops, (id) => {
      const p = by.get(id);
      // FRACTIONS, not plan pixels: the canvas multiplies them onto the rectangle the plan was
      // actually drawn in, which is the only box that knows how big the dock made it.
      return p ? { x: p.x, y: p.y } : null;
    });
  }, [stops, detail]);

  const geoPath = useMemo<Path | null>(() => {
    if (!stops.length || !geo) return null;
    const by = new Map(geo.map((p) => [p.camera_id, p]));
    return buildPath(stops, (id) => {
      const p = by.get(id);
      return p ? { x: p.lng, y: p.lat } : null;
    });
  }, [stops, geo]);

  /**
   * Which canvas opens, decided by the trail rather than by a preference.
   *
   * Only until the operator touches the toggle: `view` non-null is their choice, and a later
   * trail must not overrule it. Someone who switched to Map to see two sites is still asking
   * about two sites on the next subject.
   */
  /**
   * A canvas is offerable only when it has BOTH the trail's points and something to draw them
   * on. `geoMap.config()` is newer than the services around it, so a node that predates it
   * 404s and leaves the Map with coordinates and no basemap configuration — offered, clicked,
   * and permanently "Loading the map…". The plan is the same bargain in reverse: the image may
   * fail while the placements load, and there the pins ARE still worth drawing, because the
   * shape of the route survives a missing backdrop.
   */
  const gisReady = (gisPath?.placed || 0) > 0 && !!detail;
  const mapReady = (geoPath?.placed || 0) > 0 && !!geoConfig;

  const auto: PathView = (geoPath?.placed || 0) > (gisPath?.placed || 0) ? 'map' : 'gis';
  /** The operator's choice, else the trail's own, else whichever is actually drawable. */
  const preferred: PathView = view ?? auto;
  const active: PathView = (preferred === 'gis' && !gisReady && mapReady) ? 'map'
    : (preferred === 'map' && !mapReady && gisReady) ? 'gis'
      : preferred;
  const path = active === 'gis' ? gisPath : geoPath;

  const layoutName = layoutsOnRoute.find((l) => l.id === bestLayoutId)?.name || null;
  /**
   * EVERY lookup has answered — the empty state may not state a verdict before this.
   *
   * The plan is a second fetch, fired only once the first has named which layout to ask for, so
   * "the placements are in" is not the same as "the canvas is in". Leaving it out of this gate
   * meant that for the length of that request the panel declared none of the trail's stops
   * placed, while the tally underneath said how many were.
   */
  const loaded = placedCameras !== null && geo !== null
    && (bestLayoutId === null || layoutSettled);
  /** Has ANYBODY placed a camera on this node, on either canvas? See the empty state. */
  const anyPlacements = (placedCameras ? Object.keys(placedCameras).length : 0) > 0
    || (geo?.length || 0) > 0;

  // Frame the geographic path whenever it changes — the map has no viewBox to do it for us.
  useEffect(() => {
    if (active !== 'map' || !geoPath?.line.length) return;
    mapRef.current?.fitTo(geoPath.line.map((p) => ({ lat: p.y, lng: p.x })));
  }, [active, geoPath]);

  const nodeTitle = useCallback((n: PathNode) => {
    const name = cameraName(n.cameraId) || n.cameraId.slice(0, 8);
    const at = n.ordinals.length > 1
      ? `stops ${n.ordinals.join(' and ')}`
      : `stop ${n.ordinals[0]}`;
    return `${name} — ${at}`;
  }, [cameraName]);

  /**
   * The canvas controls, handed to the shell's header: which canvas, which floor plan, and the
   * tally that explains an empty one. They belong up there rather than over the drawing, which
   * is the thing the operator opened the dock to look at.
   */
  const canvasControls = stops.length > 0 && oneSubject ? (
          <>
            <div className="flex overflow-hidden rounded-md border border-border" role="radiogroup" aria-label="Path canvas">
              {([
                {
                  key: 'gis' as PathView, label: 'GIS', Icon: MapIcon,
                  placed: gisPath?.placed || 0, ready: gisReady,
                  none: (gisPath?.placed || 0) === 0
                    ? 'None of this trail’s cameras are pinned on a floor plan'
                    : 'That floor plan could not be loaded',
                },
                {
                  key: 'map' as PathView, label: 'Map', Icon: GlobeAltIcon,
                  placed: geoPath?.placed || 0, ready: mapReady,
                  none: (geoPath?.placed || 0) === 0
                    ? 'None of this trail’s cameras have coordinates'
                    : 'This node has no Map View configuration',
                },
              ]).map(({ key, label, Icon, placed, ready, none }) => (
                <button
                  key={key}
                  type="button"
                  role="radio"
                  aria-checked={active === key}
                  disabled={!ready}
                  onClick={() => setView(key)}
                  title={ready ? `${placed} of ${stops.length} stops on this canvas` : none}
                  className={[
                    'flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium transition-colors',
                    'disabled:cursor-not-allowed disabled:opacity-40',
                    active === key ? 'bg-brand text-white' : 'text-foreground hover:bg-muted',
                  ].join(' ')}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {label}
                </button>
              ))}
            </div>

            {/*
              The tally, whenever nothing is drawn.
              
              A panel that can only say "nothing to show" is a panel nobody can debug from the
              outside: the reader cannot tell a trail whose cameras happen to be unplaced from a
              node where placement was never done, and neither can anyone they report it to.
              Two numbers settle it, and they belong in the HEADER so they are readable without
              opening anything.
            */}
            {loaded && !path?.placed && (
              <span className="w-full text-[11px] text-muted-foreground sm:w-auto">
                {Object.keys(placedCameras || {}).length} camera
                {Object.keys(placedCameras || {}).length === 1 ? '' : 's'} pinned on a plan
                {' · '}{geo?.length || 0} with coordinates
                {' · '}this trail visits {cameraIds.length}
              </span>
            )}

            {/*
              NO CONNECTION TO DRAW, said out loud.
              
              One stop on a canvas is a pin, not a route, and a panel called "Tracking path"
              showing a single dot reads as a path that failed to render. It has not: the rest of
              the route is somewhere else, and where is exactly what the picker beside this says.
            */}
            {path && path.placed === 1 && path.total > 1 && (
              <span className="w-full text-[11px] text-amber-500 sm:w-auto">
                Only 1 of {path.total} stops is {active === 'gis' ? 'on this plan' : 'on the map'} —
                no line to draw here
                {active === 'gis' && layoutsOnRoute.length > 1 ? '. Try another plan.' : '.'}
              </span>
            )}

            {/* The coverage statement. Never omitted — see the file header. */}
            {path && path.placed > 1 && (
              <span className="w-full text-[11px] text-muted-foreground sm:w-auto">
                {active === 'gis' && layoutName && <span className="text-foreground">{layoutName} · </span>}
                {path.placed} of {path.total} stops drawn
                {path.placed < path.total && (
                  <span className="text-amber-500">
                    {/* On a route that crosses plans the missing stops are not unplaced, they
                        are ON ANOTHER PLAN — and telling an operator a camera has no pin when
                        it has one upstairs sends them to fix something that is not broken. */}
                    {active === 'gis' && layoutsOnRoute.length > 1
                      ? ` — ${path.total - path.placed} on other plans, so the line skips them`
                      : ` — ${path.total - path.placed} at cameras with no ${
                        active === 'gis' ? 'pin on this plan' : 'coordinates'
                      }, so the line skips them`}
                  </span>
                )}
              </span>
            )}
          </>
  ) : null;

  return (
    <DockShell
      storageKey="trackingPath"
      title="Tracking path"
      sub={stops.length
        ? `${stops.length} ${stops.length === 1 ? 'stop' : 'stops'}${
          identities > 1 ? ` · ${identities} different subjects` : ''}${
          byDescription ? ' · by description' : ''}`
        : 'Nothing to draw'}
      meta={path && path.placed > 0 ? `${path.placed} of ${path.total} drawn` : undefined}
      actions={canvasControls}
      defaultOpen
    >
      {/*
        `overflow-auto`, so nothing can be clipped unreachably. Both canvases fit themselves to
        this box, so in the ordinary case there is nothing to scroll — but the two empty states
        are paragraphs, and at the minimum height on a narrow window they are taller than the
        panel. Unscrollable, the sentence explaining why there is no route would itself be cut in
        half.
      */}
      {() => (
        <div
          /* `flex-1 min-h-0` inside the shell's flex body: the height comes from the layout in
             both the docked and the full-screen case, which is what the canvas measures itself
             against. A percentage height here would resolve to zero in full screen, and a canvas
             measuring zero draws nothing — the defect this replaced. */
          /* `overflow-auto` is for the TEXT states, which are paragraphs and can outgrow a
             short dock. The canvases never scroll — they are absolutely positioned and clipped,
             precisely so a scrollbar cannot appear under a box whose size they are measuring.
             That loop is what made the plan flicker. */
          className="relative min-h-0 flex-1 overflow-auto border-t border-border"
        >
          {/*
            THE CAPTION A DESCRIPTION-TRAIL MUST CARRY.

            A line between pins means "this moved from here to here". When the stops are held
            together by an appearance rather than an identity, that meaning is a claim the data
            does not make — so the drawing is given the caveat rather than withheld. Overlaid
            rather than stacked above the canvas: it is a note about the drawing, and taking a
            row of height from a dock the operator sized would be charging them for it.
          */}
          {byDescription && stops.length > 0 && (
            <div className="pointer-events-none absolute inset-x-0 top-0 z-10 bg-card px-4 py-1 text-[11px] text-muted-foreground">
              Trail of {tracks} separate tracks matching this description — whether they are one
              {' '}{subjectWord} is not something the cameras can confirm.
            </div>
          )}
          {error && !path?.placed ? (
            <div className="flex h-full items-center justify-center px-6 text-center text-[12px] text-muted-foreground">
              {error}
            </div>
          ) : !stops.length ? (
            <div className="flex h-full items-center justify-center px-6 text-center text-[12px] text-muted-foreground">
              A path is a route through the stops above. Once the results hold some, it is drawn here.
            </div>
          ) : !oneSubject ? (
            /*
             * The refusal. A line through six people's stops is not a route — it is a line
             * between strangers, drawn in the one visual language that means "this thing moved
             * from here to here". Narrowing is the operator's move, and the page already has
             * the controls for it, so this says what is wrong rather than offering a new one.
             */
            <div className="flex h-full flex-col items-center justify-center gap-1.5 px-6 text-center">
              <MapPinIcon className="h-5 w-5 text-muted-foreground/50" />
              <p className="text-[12px] font-medium text-foreground">
                These results hold {identities} different subjects.
              </p>
              <p className="max-w-md text-[11px] leading-relaxed text-muted-foreground">
                A path is one thing moving. Joining these stops with a line would draw one
                person’s doorway to another’s and read as a single journey. Narrow to one — a
                name, a plate, or an appearance only one of them has — and the route appears.
              </p>
            </div>
          ) : !loaded ? (
            /* BEFORE the verdict, never during it. The empty state below states as a fact that
               nothing on this node is placed, and saying that while still looking would be a
               confident wrong answer shown on every single open. */
            <div className="flex h-full items-center justify-center text-[12px] text-muted-foreground">
              {bestLayoutId && !layoutSettled
                ? `Loading ${layoutName || 'the floor plan'}…`
                : 'Finding where these cameras are…'}
            </div>
          ) : !path || path.placed === 0 ? (
            /**
             * WHICH of the two reasons it is, because they are fixed in different places.
             *
             * "Nobody has placed ANY camera on this node" is a deployment that has not set the
             * feature up; "none of THESE cameras are placed" is a deployment that has, and
             * missed these. Telling them apart is the difference between an operator going to
             * find the person who owns the floor plans and an operator deciding the panel is
             * broken.
             */
            <div className="flex h-full flex-col items-center justify-center gap-1.5 px-6 text-center">
              <MapPinIcon className="h-5 w-5 text-muted-foreground/50" />
              <p className="text-[12px] font-medium text-foreground">
                {anyPlacements
                  ? `None of this trail’s ${path?.total ?? 0} stops are at a placed camera.`
                  : 'No camera on this node has been placed yet.'}
              </p>
              <p className="max-w-md text-[11px] leading-relaxed text-muted-foreground">
                {anyPlacements
                  ? 'A camera is drawn here once it is pinned on a floor plan or given '
                    + 'coordinates. These ones are neither, so the trail is complete — it just '
                    + 'has nowhere to be drawn.'
                  : 'Pin cameras on a plan under Alert Board ▸ GIS View, or give them '
                    + 'coordinates under Alert Board ▸ Map View. Until one of those is done '
                    + 'there is no canvas to draw a route on, for any subject.'}
              </p>
              <p className="text-[11px] text-muted-foreground">
                {Object.keys(placedCameras || {}).length} pinned on a plan
                {' · '}{geo?.length || 0} with coordinates
              </p>
            </div>
          ) : active === 'gis' && detail ? (
            // `image_width`/`image_height` are the space the placements are fractions OF, so a
            // layout stored without them has no coordinate system and every pin would land on
            // 0,0. Said plainly rather than drawn as an empty grey box.
            detail.image_width > 0 && detail.image_height > 0 ? (
              <div className="flex h-full">
                {/*
                  ── THE PLANS THIS ROUTE ACTUALLY TOUCHES, DOWN THE SIDE ──

                  A camera belongs to exactly one layout, so a route through a building is split
                  across floors and no single plan holds all of it. This used to be a `select` in
                  the dock's title bar: three clicks to compare two floors, and — being a closed
                  control — it never showed that the other floors existed at all until you opened
                  it. On a panel whose whole subject is WHERE, "the rest of this route is
                  upstairs" is not a detail to hide behind a dropdown.

                  ONLY THE PLANS WITH STOPS ON THEM are listed. A site may hold a dozen layouts;
                  the ones this trail never reached are not choices, they are noise, and offering
                  them would invite an operator to open a plan that is empty by construction and
                  read that as the route having been lost. The count beside each name is what
                  makes the split legible without opening anything.

                  Only the CHOSEN plan's image is ever fetched — the list is names and counts,
                  which the placements already carried.
                */}
                {layoutsOnRoute.length > 1 && (
                  <nav
                    aria-label="Floor plans on this route"
                    /* Narrow on a phone, where every pixel it takes is one the plan loses; the
                       names truncate and the counts stay, which is the part that cannot be
                       guessed from the drawing. */
                    className="flex w-28 shrink-0 flex-col overflow-y-auto border-r border-border bg-card sm:w-40"
                  >
                    <p className="sticky top-0 bg-card px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {layoutsOnRoute.length} plans on this route
                    </p>
                    <ul className="pb-1">
                      {layoutsOnRoute.map((l) => {
                        const current = l.id === bestLayoutId;
                        return (
                          <li key={l.id}>
                            <button
                              type="button"
                              onClick={() => setChosenLayout(l.id)}
                              aria-current={current ? 'true' : undefined}
                              title={`${l.stops} of this trail's ${stops.length} stops are on ${l.name}`}
                              className={`flex w-full items-baseline gap-2 border-l-2 px-2.5 py-1.5 text-left transition-colors ${
                                current
                                  ? 'border-brand bg-brand-light'
                                  : 'border-transparent hover:bg-muted'
                              }`}
                            >
                              <span
                                className={`min-w-0 flex-1 truncate text-[11px] ${
                                  current ? 'font-semibold text-foreground' : 'text-foreground'
                                }`}
                              >
                                {l.name}
                              </span>
                              <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground">
                                {l.stops}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </nav>
                )}

                {/* The positioning context the canvas measures itself against — see PlanPath. */}
                <div className="relative min-w-0 flex-1">
                  <PlanPath
                    path={gisPath!}
                    planUrl={planUrl}
                    width={detail.image_width}
                    height={detail.image_height}
                    nodeTitle={nodeTitle}
                  />
                </div>
              </div>
            ) : (
              <div className="flex h-full items-center justify-center px-6 text-center text-[12px] text-muted-foreground">
                {layoutName || 'This floor plan'} has no stored image size, so its pins have no
                coordinates to sit on. Re-upload the plan under Alert Board ▸ GIS View.
              </div>
            )
          ) : active === 'map' && geoConfig ? (
            <>
              <GeoTileMap
                handleRef={mapRef}
                source={basemap.source}
                initialView={{
                  lat: geoConfig.center_lat,
                  lng: geoConfig.center_lng,
                  zoom: geoConfig.zoom,
                }}
                className="absolute inset-0"
                pins={(project) => <GeoPath path={geoPath!} project={project} nodeTitle={nodeTitle} />}
              />
              {/* Above the map's credits button, clear of the description-trail caption. */}
              <BasemapSwitcher
                value={basemap.id}
                onChange={basemap.setId}
                className="absolute bottom-11 right-2 z-20"
              />
            </>
          ) : (
            /* The lookups have SETTLED and there is still no canvas — the plan image or the Map
               View configuration did not come back. Said as a fact rather than left spinning:
               a permanent "Loading…" is the one state that tells the operator nothing and
               invites them to keep waiting. */
            <div className="flex h-full items-center justify-center px-6 text-center text-[12px] text-muted-foreground">
              The {active === 'gis' ? 'floor plan' : 'map'} could not be loaded, so this trail’s
              {' '}{path.placed} placed {path.placed === 1 ? 'stop has' : 'stops have'} nowhere to
              be drawn.
            </div>
          )}
        </div>
      )}
    </DockShell>
  );
}

// ─── The two canvases ───────────────────────────────────────────────────────

/** Undoes GeoTileMap's in-flight zoom stretch about the element's own origin. */
const UNSCALE = 'scale(calc(1 / var(--map-k, 1)))';

/** Shared ink, so a route means the same thing on either canvas. */
const LINE = 'var(--brand)';
const START = '#10b981';
const END = 'var(--brand)';

/**
 * The plan canvas — an SVG framed on the path, with the plan behind it.
 *
 * No pan or zoom. The panel is fifteen rems tall and the frame is computed from the path
 * itself, so there is nothing for a reader to navigate to; a pan control here would be a
 * control whose only use is undoing the framing the panel just did for them.
 */
function PlanPath({
  path, planUrl, width, height, nodeTitle,
}: {
  path: Path;
  planUrl: string | null;
  width: number;
  height: number;
  nodeTitle: (n: PathNode) => string;
}) {
  /**
   * THE CANVAS IS SIZED FROM ITS CONTAINER, IN PIXELS.
   *
   * It used to size itself: `h-full w-full` on the `<svg>`, and a ResizeObserver on that same
   * element to read back the shape the crop had to match. That is a circle, and it breaks
   * wherever the percentage height fails to resolve — which is every moment the chain above it
   * has no definite height: the first paint before the dock is measured, the switch into full
   * screen, the frame after a drag. The element then reports a height of zero, and a canvas that
   * measures zero draws nothing — which is what "the overlay is not rendering properly" looks
   * like.
   *
   * So the CONTAINER is measured instead, and the svg is given explicit `width` and `height`
   * attributes from it. Nothing about the drawing depends on CSS resolving a percentage any
   * more: the box is a number, the viewBox is computed from that number, and the two cannot
   * disagree.
   */
  const boxRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });

  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;

    const read = () => {
      // `clientWidth/Height`, not the bounding rect: the rect includes any border and is affected
      // by a CSS transform, and an svg sized a pixel larger than its box is an svg with a
      // scrollbar next to it.
      const w = el.clientWidth;
      const h = el.clientHeight;
      /**
       * A ZERO IS NOT A SIZE, it is a moment.
       *
       * The box reports 0×0 while the dock is folding, between a tab switch and the layout that
       * follows it, and on the frame a parent is re-created. Taking those literally blanks the
       * canvas — `size.w > 0` guards the draw — and then restores it one frame later, which is
       * a flicker with no cause the operator can see. The last real measurement is still the
       * best answer until a new real one arrives.
       */
      if (w === 0 || h === 0) return;
      setSize((prev) => (
        // Sub-pixel churn is not a change worth a render, and re-rendering from inside a
        // ResizeObserver on every tick is how an observer loop starts.
        Math.abs(prev.w - w) < 1 && Math.abs(prev.h - h) < 1 ? prev : { w, h }
      ));
    };
    read();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', read);
      return () => window.removeEventListener('resize', read);
    }
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /**
   * IS THE PLAN BEING DRAWN STRETCHED, and if so, say so.
   *
   * The image is drawn with `preserveAspectRatio="none"` into `image_width × image_height`,
   * because the placements are fractions OF those numbers — a pin only lands on the door it was
   * put on if the plan occupies exactly that box. The GIS editor draws it the same way, so the
   * two agree.
   *
   * But when the stored numbers do not match the file (a re-upload at a different size, an
   * export scaled on the way out), that agreement is a distortion: the plan is squashed or
   * pulled, and at any magnification it looks wrong in a way no amount of framing can fix. The
   * operator cannot see WHY from the picture, so the panel measures the file and tells them —
   * with the one action that fixes it, which is re-uploading the plan against its real size.
   */
  const [naturalAspect, setNaturalAspect] = useState(0);
  useEffect(() => {
    setNaturalAspect(0);
    if (!planUrl || typeof Image === 'undefined') return undefined;
    let live = true;
    const probe = new Image();
    probe.onload = () => {
      if (live && probe.naturalWidth > 0 && probe.naturalHeight > 0) {
        setNaturalAspect(probe.naturalWidth / probe.naturalHeight);
      }
    };
    // A plan that will not load is already reported by the panel's own empty states.
    probe.src = planUrl;
    return () => { live = false; };
  }, [planUrl]);

  // 5%: below that the difference is a rounding artefact of the upload, not something an
  // operator would see or should be nagged about.
  const stretched = naturalAspect > 0 && height > 0
    && Math.abs(naturalAspect / (width / height) - 1) > 0.05;
  /**
   * The plan's rectangle inside this container, and the route mapped onto it.
   *
   * EVERYTHING IS IN CONTAINER PIXELS from here down — the svg's viewBox is the container, so a
   * unit is a pixel and nothing is scaled twice. The placements arrive as fractions of the plan,
   * which is what they are stored as, and are multiplied onto the drawn rectangle: the route
   * therefore lands wherever the plan landed, at whatever size the dock gave it.
   */
  const rect = fitPlan(size, { w: width, h: height });

  const nodes = useMemo(
    () => (rect ? path.nodes.map((n) => ({ ...n, ...placeOn(rect, n) })) : []),
    [rect?.x, rect?.y, rect?.w, rect?.h, path.nodes],
  );

  /** The legs, re-pointed at the placed pins. `edgeGeometry` reads only coordinates. */
  const edges = useMemo(() => {
    if (!rect) return [];
    const byId = new Map(nodes.map((n) => [n.cameraId, n]));
    return path.edges
      .map((e) => {
        const from = byId.get(e.from.cameraId);
        const to = byId.get(e.to.cameraId);
        return from && to ? { ...e, from, to } : null;
      })
      .filter(Boolean) as typeof path.edges;
  }, [nodes, path.edges, rect]);

  /**
   * The pin's size, from the DRAWN plan rather than from the stored one.
   *
   * Clamped at both ends: on a plan drawn small a proportional pin is a dot with unreadable
   * digits in it, and on a large one it becomes a saucer covering the room it marks.
   */
  const r = rect ? Math.max(9, Math.min(26, Math.min(rect.w, rect.h) / 26)) : 12;

  return (
    /*
      ── ABSOLUTE, SO THE CANVAS CANNOT MEASURE ITSELF INTO A LOOP ──

      The svg is given explicit pixel dimensions read off this box. In normal flow inside a
      scrollable parent that is a feedback loop waiting to happen: an svg a pixel wider than the
      content box raises a scrollbar, the scrollbar narrows the content box, the next measurement
      shrinks the svg, the scrollbar goes, the box widens — and the plan flickers at the refresh
      rate of the observer.

      Taking the canvas out of flow ends it. An absolutely positioned box is exactly the padding
      box of its container, contributes nothing to the scroll extent, and therefore cannot change
      the thing it is measuring. `overflow-hidden` is the belt to that braces.
    */
    <div ref={boxRef} className="absolute inset-0 overflow-hidden bg-muted">
      {stretched && (
        <p className="pointer-events-none absolute inset-x-0 bottom-0 z-10 bg-card px-3 py-1 text-[10px] leading-tight text-muted-foreground">
          This plan is stored as {width}×{height}, but the image file is
          {' '}{naturalAspect > width / height ? 'wider' : 'taller'} than that — so it is drawn
          stretched to keep the pins on the features they were placed on. Re-upload it under
          Alert Board ▸ GIS View to fix the shape.
        </p>
      )}
      {/* Drawn only once there IS a box to draw into. A pass at zero would emit a viewBox of
          NaNs, and the browser's answer to that is an empty element that looks exactly like a
          plan which failed to load. */}
      {size.w > 0 && size.h > 0 && rect && (
    <svg
      width={size.w}
      height={size.h}
      viewBox={`0 0 ${size.w} ${size.h}`}
      /* `bg-muted`, not `bg-muted/30`: these palette tokens are bare `var()` values and
         Tailwind v3 drops the opacity modifier on those silently — the canvas had no
         background at all, so a plan that failed to load left the dock looking empty rather
         than blank-but-present. */
      className="block bg-muted"
      role="img"
      aria-label={`The route across ${path.nodes.length} cameras on the floor plan`}
    >
      {/*
        `preserveAspectRatio="none"`, which is SVG for the editor's `object-fill`.
        
        The placements are FRACTIONS of `image_width × image_height`, so the plan has to occupy
        exactly that box for a pin to land on the door it was put on. The default —
        `xMidYMid meet` — fits the image INSIDE the box instead, and wherever the stored
        dimensions disagree with the file's own aspect (a re-upload, a resized export) that
        inset the drawing while leaving the pins where they were: a plan that looks the wrong
        size with its markers floating off the features.
        
        Matching the editor is not a stylistic choice here. GIS View draws it stretched, so a
        route drawn any other way would put the same camera in two different places in two
        parts of the product.
      */}
      {planUrl && (
        <image
          href={planUrl}
          x={rect.x}
          y={rect.y}
          width={rect.w}
          height={rect.h}
          preserveAspectRatio="none"
        />
      )}

      {/* Every LEG, drawn separately and bowed — see `PathEdge`. A single polyline hid the
          return half of every there-and-back, which is most of what a trail is. */}
      {edges.map((e) => {
        const g = edgeGeometry(e);
        return (
          <g key={`${e.seq}-${e.from.cameraId}-${e.to.cameraId}`}>
            <title>{`${e.seq - 1} → ${e.seq}`}</title>
            {/* A pale casing under the ink. A floor plan is line art in the same greys as the
                route, and without it a leg crossing a wall disappears into it. */}
            <path d={g.d} fill="none" stroke="var(--card)" strokeWidth={r * 0.46} strokeLinecap="round" opacity={0.85} />
            <path d={g.d} fill="none" stroke={LINE} strokeWidth={r * 0.22} strokeLinecap="round" />
            {/* The arrowhead carries the DIRECTION, which is the whole difference between a
                route and a set of lines. At the midpoint, not the end, where the pin would
                cover it. */}
            <path
              d={`M ${-r * 0.34} ${-r * 0.26} L ${r * 0.3} 0 L ${-r * 0.34} ${r * 0.26} Z`}
              transform={`translate(${g.mx} ${g.my}) rotate(${g.angle})`}
              fill={LINE}
            />
          </g>
        );
      })}

      {nodes.map((n) => {
        const first = n.ordinals.includes(1);
        const last = n.ordinals.includes(path.total);
        return (
          <g key={n.cameraId}>
            <title>{nodeTitle(n)}</title>
            <circle
              cx={n.x}
              cy={n.y}
              r={r}
              fill={first ? START : last ? END : 'var(--card)'}
              stroke={first ? START : END}
              strokeWidth={r * 0.18}
            />
            <text
              x={n.x}
              y={n.y}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={r * 1.05}
              fontWeight={700}
              fill={first || last ? '#fff' : 'var(--foreground)'}
            >
              {n.ordinals.join(',')}
            </text>
            {/* The camera's name under its pin. The whole point of a plan is that a route reads
                as places rather than as ids, so the names are ON it, not in a tooltip. */}
            <text
              x={n.x}
              y={n.y + r * 1.9}
              textAnchor="middle"
              fontSize={r * 0.62}
              fill="var(--foreground)"
              stroke="var(--card)"
              strokeWidth={r * 0.14}
              paintOrder="stroke"
            >
              {nodeTitle(n).split(' — ')[0]}
            </text>
          </g>
        );
      })}
      <text x={size.w / 2} y={size.h} className="sr-only">
        {`${path.placed} of ${path.total} stops`}
      </text>
    </svg>
      )}
    </div>
  );
}

/**
 * The geographic canvas — the same path, projected into the map's layer space.
 *
 * Marks are a CONSTANT SCREEN SIZE here, unlike the plan's. Between settles GeoTileMap
 * stretches the whole layer to follow an animating zoom (`--map-k`), so the geometry rides
 * along exactly while strokes stay `non-scaling-stroke` and every stop badge and arrowhead
 * counter-scales about its own point — the route never thickens or balloons mid-zoom.
 */
function GeoPath({
  path, project, nodeTitle,
}: {
  path: Path;
  project: (lat: number, lng: number) => { x: number; y: number };
  nodeTitle: (n: PathNode) => string;
}) {
  /**
   * The edges re-laid in SCREEN space.
   *
   * The bow is a fraction of the leg's length, so it has to be computed after projection or a
   * zoom would change the curvature — two cameras a street apart would bow like a hairpin at
   * one zoom and like a straight line at another.
   */
  const screen = path.edges.map((e) => ({
    seq: e.seq,
    key: `${e.seq}-${e.from.cameraId}-${e.to.cameraId}`,
    ...edgeGeometry({
      ...e,
      from: { ...e.from, ...project(e.from.y, e.from.x) },
      to: { ...e.to, ...project(e.to.y, e.to.x) },
    }),
  }));

  return (
    <svg
      className="pointer-events-none absolute left-0 top-0 overflow-visible"
      width={1}
      height={1}
      role="img"
      aria-label={`The route across ${path.nodes.length} cameras on the map`}
    >
      {screen.map((g) => (
        <g key={g.key}>
          <title>{`${g.seq - 1} → ${g.seq}`}</title>
          <path d={g.d} fill="none" stroke="var(--card)" strokeWidth={6} strokeLinecap="round" opacity={0.85} vectorEffect="non-scaling-stroke" />
          <path d={g.d} fill="none" stroke={LINE} strokeWidth={3} strokeLinecap="round" vectorEffect="non-scaling-stroke" />
          <path
            d="M -5 -4 L 5 0 L -5 4 Z"
            style={{ transform: `translate(${g.mx}px, ${g.my}px) rotate(${g.angle}deg) ${UNSCALE}` }}
            fill={LINE}
          />
        </g>
      ))}
      {path.nodes.map((n) => {
        const at = project(n.y, n.x);
        const first = n.ordinals.includes(1);
        const last = n.ordinals.includes(path.total);
        return (
          <g
            key={n.cameraId}
            className="pointer-events-auto"
            style={{ transform: `translate(${at.x}px, ${at.y}px) ${UNSCALE}` }}
          >
            <title>{nodeTitle(n)}</title>
            <circle
              cx={0}
              cy={0}
              r={12}
              fill={first ? START : last ? END : 'var(--card)'}
              stroke={first ? START : END}
              strokeWidth={2}
            />
            <text
              x={0}
              y={0}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={11}
              fontWeight={700}
              fill={first || last ? '#fff' : 'var(--foreground)'}
            >
              {n.ordinals.join(',')}
            </text>
            <text
              x={0}
              y={24}
              textAnchor="middle"
              fontSize={10}
              fill="var(--foreground)"
              stroke="var(--card)"
              strokeWidth={2.5}
              paintOrder="stroke"
            >
              {nodeTitle(n).split(' — ')[0]}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
